import SEO from '../../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Factory, TrendingUp, Shield, BarChart3, Package, Zap, Award, Target } from 'lucide-react';
import { Button } from '../../components/ui/button';
import ProductCTA from '../../components/ProductCTA';
import PersonaNavigation from '../../components/PersonaNavigation';

const ManufacturingDirectorPage = () => {
  const stats = [
    { value: "99%", label: "Production Accuracy" },
    { value: "40%", label: "Cost Reduction" },
    { value: "Real-time", label: "Shop Floor" },
    { value: "900+", label: "Manufacturers" }
  ];

  const benefits = [
    { icon: Factory, title: "Shop Floor Control", strapline: "Real-time visibility", desc: "Monitor production orders and operations live with dashboards." },
    { icon: BarChart3, title: "Production Planning", strapline: "Optimise scheduling", desc: "Automated scheduling, capacity planning, and MRP." },
    { icon: Shield, title: "Quality Management", strapline: "FDA compliant", desc: "Full batch traceability with quality inspections." },
    { icon: Package, title: "Material Management", strapline: "Never run out", desc: "Intelligent MRP prevents material shortages." },
    { icon: TrendingUp, title: "Cost Visibility", strapline: "True costs", desc: "Capture actual costs at batch level." },
    { icon: Zap, title: "Industry 4.0", strapline: "Modern tech", desc: "Integrate IoT and automation." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One for Manufacturing Directors | Production Excellence"
        description="Production planning, quality control, shop floor visibility with SAP B1 + Beas Manufacturing. Real-time dashboards for manufacturing excellence."
        keywords="manufacturing director software, production planning, quality management, shop floor control, SAP B1 manufacturing"
        canonical="https://www.ascarii.com/sap-business-one/for/manufacturing-directors"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-slate-700 via-blue-800 to-slate-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=1920&q=85" alt="Manufacturing" className="w-full h-full object-cover opacity-40" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">For Manufacturing Directors</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">Production Excellence, Quality Assured</h1>
              <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">Achieve 99% accuracy with complete shop floor visibility and quality control.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Persona Navigation */}
      <PersonaNavigation currentPersona="manufacturing-directors" />

      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-slate-800 via-blue-900 to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Why Manufacturing Directors Choose SAP Business One</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true }} className="group p-8 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:border-cyan-300 hover:bg-white/20 transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-lg text-cyan-300 mb-3 font-medium">{benefit.strapline}</p>
                    <p className="text-blue-100 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-100 via-blue-50 to-gray-100">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your Manufacturing</h2>
              <p className="text-xl text-gray-600 mb-8">Join 900+ manufacturing directors achieving excellence</p>
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center">
                    <Factory className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Shop Floor Control</h4>
                    <p className="text-gray-600 text-sm">Complete visibility</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 flex items-center justify-center">
                    <Award className="w-7 h-7 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">Proven Success</h4>
                    <p className="text-gray-600 text-sm">900+ manufacturers</p>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="lg:col-span-3">
              <ProductCTA productName="SAP Business One for Manufacturing Directors" brandColor="blue-600" />
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManufacturingDirectorPage;
